import { Button, Check, CreditCardBrand, ProgressCircle, Site, useShowError, Viewer } from "@equiem/web-ng-lib";
import { StripePortalLink } from "./StripePortalLink";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { BsBoxArrowUpRight } from "react-icons/bs";
import { CardFragmentFragment } from "../../generated/gateway-client";
import { useTranslation } from "../../generated/localisation";

interface Props {
  selectedCard?: CardFragmentFragment;
  onHide: () => void;
  onSave: (card?: CardFragmentFragment) => Promise<void>;
}

export const PaymentCardSelector: React.FC<Props> = ({
  selectedCard: selectedCardInput,
  onHide,
  onSave,
}) => {
  const { t } = useTranslation();

  const viewer = useContext(Viewer);
  const site = useContext(Site);
  const showError = useShowError();
  const cards = viewer?.profile?.cards ?? [];
  const [saving, setSaving] = useState(false);
  const [selectedCardUuid, setSelectedCardUuid] = useState(selectedCardInput?.uuid);
  const selectedCard = useMemo(() => (
    cards.find((c) => c.uuid === selectedCardUuid)
  ), [selectedCardUuid, cards]);

  useEffect(() => {
    if (selectedCardInput != null) {
      setSelectedCardUuid(selectedCardInput.uuid);
    }
  }, [selectedCardInput]);

  return (
    <div className="payment-card-selector">
      {cards.length === 0 ? (
        <>
          <p className="mb-2">{t("main.addPaymentMethodOpensPortal")}</p>
          <StripePortalLink
            className="btn btn-link pl-0 d-inline-flex align-items-center"
          >
            {t("lib.addNewCard")}
            <BsBoxArrowUpRight className="ml-1" size={14} aria-hidden={true} />
          </StripePortalLink>
          <div className="mt-3">
            <Button variant="outline" type="button" onClick={onHide}>
              {t("lib.back")}
            </Button>
          </div>
        </>
      ) : (
        <>
          <div className="border-top">
            {cards.map((card) => (
              <div
                key={card.uuid}
                className="card-radio d-flex align-items-center py-3 px-1 border-bottom"
                onClick={() => {
                  setSelectedCardUuid(card.uuid);
                }}
              >
                <Check
                  type="radio"
                  name="selectedCard"
                  checked={selectedCardUuid === card.uuid}
                  onChange={() => {
                    setSelectedCardUuid(card.uuid);
                  }}
                />
                <div className="d-flex align-items-center">
                  <CreditCardBrand brand={card.brand} />
                  <span>**** **** **** {card.last4}</span>
                </div>
              </div>
            ))}
          </div>
          <div>
            <StripePortalLink
              className="btn btn-link pl-0 mt-2 d-inline-flex align-items-center"
            >
              {t("lib.addNewCard")}
              <BsBoxArrowUpRight className="ml-1" size={14} aria-hidden={true} />
            </StripePortalLink>
          </div>
          <div className="mt-3">
            <Button
              variant="outline"
              type="button"
              className="mr-2"
              disabled={saving}
              onClick={() => {
                setSelectedCardUuid(selectedCardInput?.uuid);
                onHide();
              }}
            >
              {t("lib.back")}
            </Button>
            <Button
              variant="solid"
              disabled={saving}
              type="button"
              onClick={() => {
                setSaving(true);
                onSave(selectedCard)
                  .then(onHide)
                  .catch((e) => {
                    showError(e);
                    setSaving(false);
                  });
              }}
            >
              {saving ? <ProgressCircle color={site.primaryContrastColour} size="sm" className="mr-1" /> : null}
              {t("lib.save")}
            </Button>
          </div>
        </>
      )}
      <style jsx>{`
        .card-radio {
          cursor: pointer;
        }
        .card-radio:hover {
          background: rgba(0, 0, 0, 0.025);
        }
      `}</style>
    </div>
  );
};
